import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Alert,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  Fab,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Checkbox,
} from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ep } from '../api/endpoints';
import * as Icons from '../constants/icons';
import { PAGE_FILL_HEIGHT } from '../constants/layout';
import StandardDataTable, { Column } from '../components/StandardDataTable';
import { usePermissions } from '../firebase/PermissionProvider';
import { useAuth } from '../firebase/AuthProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { useNotification } from '../providers/NotificationProvider';
import { STANDARD_CTA_BUTTON_SX, STANDARD_FAB_CTA_SX, STANDARD_TEXT_BUTTON_SX } from '../constants/buttonStyles';
import {
  useEventManagement,
  EventEntryRow,
  EventParticipantRow,
  EventPaymentRow,
  EventSponsorshipRow,
  EventInviteeRow,
  EventResultRow,
  EventAwardRow,
  EventAwardInput,
  EventParticipantInput,
} from '../hooks/useEventManagement';
import { useMasterData } from '../providers/MasterDataProvider';
import EventMailPanel from '../components/EventMailPanel';
import EventAssignmentPanel from '../components/EventAssignmentPanel';
import CrmCustomerForm from '../components/CrmCustomerForm';
import EventParticipantDialog, { EventParticipantEntry } from '../components/EventParticipantDialog';
import EventAwardDialog, { AwardImageChange } from '../components/EventAwardDialog';
import EventAwardSlotsDialog from '../components/EventAwardSlotsDialog';
import EventAwardOrderDialog from '../components/EventAwardOrderDialog';

/**
 * イベント詳細 (申込状況) ページ。
 *
 * イベント一覧 (/event-management) で選択したイベントについて、
 * 申込サイトが登録した申込・参加者・入金・協賛をタブ切替の一覧で表示する。
 * ヘッダー (一覧へ戻る・更新) は常時表示し、テーブル領域のみローディングを重ねる。
 *
 * 入金消込 (event.master.edit 保有者のみ)
 *   - 入金一覧タブの「入金額」セルをクリック→その場で TextField 展開→Enter/フォーカスアウトで保存。
 *     is_paid はサーバー側の到達判定 (入金額 ≧ 予定額) で自動セットされる
 *   - 「状況」チップのクリックで入金済⇔未入金を手動上書き (手数料差引等の例外承認用・確認あり)
 *
 * 賞品の授与 (event.master.edit 保有者のみ)
 *   - 賞品の追加・編集・削除、発表順と受け取りのその場編集
 *   - 「発表順を並べ替え」で全行をドラッグで並べ替え、発表順を 1 から振り直す
 *   - 賞品の画像は 1 行に 1 枚 (追加・編集ダイアログで付ける。一覧のサムネイルのクリックで拡大)
 *   - 「協賛品から賞品枠を作る」で当選者未定の賞品枠を作り、当選者は後から入れる (一括取込は AI で行う)
 */

const yen = (v: number | null | undefined): string => (v == null ? '' : `¥${Number(v).toLocaleString()}`);

// ○ 表示 (boolean 列の視認性優先。false/null は空欄)
const circle = (v: boolean | null | undefined): string => (v ? '○' : '');

const attendanceChip = (attendance: string) => (
  <Chip
    size="small"
    label={attendance}
    sx={{
      fontSize: '11px',
      height: '20px',
      fontWeight: 700,
      ...(attendance === '出席'
        ? { bgcolor: '#ecfdf5', color: '#059669' }
        : { bgcolor: '#f1f5f9', color: '#64748b' }),
    }}
  />
);

const paidChip = (isPaid: boolean | null | undefined) => {
  if (isPaid == null) return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>-</Typography>;
  return (
    <Chip
      size="small"
      label={isPaid ? '入金済' : '未入金'}
      sx={{
        fontSize: '11px',
        height: '20px',
        fontWeight: 700,
        ...(isPaid ? { bgcolor: '#ecfdf5', color: '#059669' } : { bgcolor: '#fef3c7', color: '#92400e' }),
      }}
    />
  );
};

// 入金額のインライン編集セル。クリックでセル位置に TextField を展開し、
// Enter / フォーカスアウトで確定保存、Escape でキャンセルする。
// 編集状態はセル内に閉じ込め、確定時のみ onSave (API 呼び出し) を実行する。
const PaidAmountCell = React.memo(function PaidAmountCell({
  row,
  canEdit,
  onSave,
}: {
  row: EventPaymentRow;
  canEdit: boolean;
  onSave: (paymentId: number, paidAmount: number | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  // Enter 確定直後の blur で二重保存しないためのガード
  const committingRef = useRef(false);

  const startEdit = () => {
    if (!canEdit || saving) return;
    setValue(row.paid_amount == null ? '' : String(row.paid_amount));
    committingRef.current = false;
    setEditing(true);
  };

  const cancel = () => {
    committingRef.current = true;
    setEditing(false);
  };

  const commit = async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    setEditing(false);
    const trimmed = value.trim();
    const newVal = trimmed === '' ? null : Number(trimmed);
    if (newVal !== null && (!Number.isFinite(newVal) || newVal < 0)) return;
    if (newVal === (row.paid_amount ?? null)) return;
    setSaving(true);
    await onSave(row.id, newVal);
    setSaving(false);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        type="number"
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') cancel();
        }}
        sx={{ width: 110, '& .MuiInputBase-input': { fontSize: '13px', py: 0.5, textAlign: 'right' } }}
      />
    );
  }

  return (
    <Box
      onClick={startEdit}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        minHeight: 24,
        ...(canEdit && {
          cursor: 'pointer',
          borderRadius: '6px',
          px: 0.75,
          py: 0.25,
          '&:hover': { bgcolor: '#eff6ff', '& .edit-hint': { opacity: 1 } },
        }),
      }}
    >
      {saving ? (
        <CircularProgress size={14} />
      ) : (
        <>
          {row.paid_amount == null ? (
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>{canEdit ? '未入力' : ''}</Typography>
          ) : (
            <Typography sx={{ fontSize: '13px' }}>{yen(row.paid_amount)}</Typography>
          )}
          {canEdit && (
            <Icons.Edit className="edit-hint" sx={{ fontSize: '13px', color: '#94a3b8', opacity: 0, transition: 'opacity 0.15s' }} />
          )}
        </>
      )}
    </Box>
  );
});

// 検索入力。毎キー入力での全テーブル再描画を避けるため、ローカル state に閉じ込めて
// デバウンス確定時のみ親の filter を更新する (list-search-input-isolation)
const SearchInput = React.memo(function SearchInput({ onCommit }: { onCommit: (v: string) => void }) {
  const [value, setValue] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleChange = (v: string) => {
    setValue(v);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onCommit(v), 300);
  };

  return (
    <TextField
      size="small"
      placeholder="受付NO・会社名・氏名などで検索"
      value={value}
      onChange={(e) => handleChange(e.target.value)}
      sx={{ minWidth: 280, backgroundColor: '#fff', '& .MuiInputBase-input': { fontSize: '13px', py: 0.75 } }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Icons.Search sx={{ fontSize: '18px', color: '#94a3b8' }} />
          </InputAdornment>
        ),
      }}
    />
  );
});

type TabKey =
  | 'entries'
  | 'participants'
  | 'payments'
  | 'sponsorships'
  | 'invitees'
  | 'results'
  | 'teamResults'
  | 'awards'
  | 'assignments'
  | 'mail';

// 開催後の記録のタブ (配置タブの後ろに並べる)
const RECORD_TAB_KEYS: TabKey[] = ['results', 'teamResults', 'awards'];

// 成績タブの固定列 (順位 / 氏名) の内部幅。見出しと値のセルで共有して桁を揃える
const RANK_STICKY_GRID = '44px minmax(0, 1fr)';

const personName = (r: { last_name: string | null; first_name: string | null }): string =>
  `${r.last_name ?? ''} ${r.first_name ?? ''}`.trim();

// 成績の数値表示 (未登録は空欄)
const scoreText = (v: string | number | null | undefined): string => (v == null || v === '' ? '' : String(v));

const awardTypeChip = (awardType: string) => (
  <Chip
    size="small"
    label={awardType}
    sx={{
      fontSize: '11px',
      height: '20px',
      fontWeight: 700,
      ...(awardType === '表彰' ? { bgcolor: '#eff6ff', color: '#3b82f6' } : { bgcolor: '#fffbeb', color: '#d97706' }),
    }}
  />
);

// 申込一覧の固定列 (受付NO / 出欠 / 会社名) の内部幅。見出しと値のセルで共有して桁を揃える
const ENTRY_STICKY_GRID = '44px 56px minmax(0, 1fr)';

// 受付NO + 会社名を1列に固定するタブ (参加者一覧 / 入金一覧 / 協賛品一覧) の内部幅。
// 見出しと値のセルで共有して桁を揃える
const RECEPTION_STICKY_GRID = '44px minmax(0, 1fr)';

// 申込一覧タブの出欠フィルター
type EntryFilterKey = 'all' | 'attend' | 'absent';

const ENTRY_FILTERS: { key: EntryFilterKey; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'attend', label: '出席' },
  { key: 'absent', label: '欠席' },
];

const matchEntryFilter = (r: EventEntryRow, f: EntryFilterKey): boolean => {
  switch (f) {
    case 'attend':
      return r.attendance === '出席';
    case 'absent':
      return r.attendance === '欠席';
    default:
      return true;
  }
};

// 賞品の授与タブの絞り込み (当選者未定の賞品枠・受け取り前の賞品を探す)
type AwardFilterKey = 'all' | 'no_winner' | 'not_received';

const AWARD_FILTERS: { key: AwardFilterKey; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'no_winner', label: '当選者未定' },
  { key: 'not_received', label: '受け取り前' },
];

const matchAwardFilter = (r: EventAwardRow, f: AwardFilterKey): boolean => {
  switch (f) {
    case 'no_winner':
      return r.participant_id == null;
    case 'not_received':
      return r.participant_id != null && !r.is_received;
    default:
      return true;
  }
};

// 配信予定タブの案内区分フィルター
type InviteeFilterKey = 'all' | 'first' | 'bulk' | 'self' | 'no_invite' | 'coop' | 'company_only';

const INVITEE_FILTERS: { key: InviteeFilterKey; label: string }[] = [
  { key: 'all', label: 'すべて' },
  { key: 'first', label: '1次案内希望' },
  { key: 'bulk', label: '一斉メール希望' },
  { key: 'self', label: '自分で案内' },
  { key: 'no_invite', label: '案内なし' },
  { key: 'coop', label: '協力会正会員' },
  { key: 'company_only', label: '法人宛のみ' },
];

const matchInviteeFilter = (r: EventInviteeRow, f: InviteeFilterKey): boolean => {
  switch (f) {
    case 'first':
      return !!r.request_first_mail;
    case 'bulk':
      return !!r.request_bulk_mail;
    case 'self':
      return !!r.self_invite;
    case 'no_invite':
      return !!r.no_invite;
    case 'coop':
      return !!r.coop_regular_member;
    case 'company_only':
      return !r.customer_id;
    default:
      return true;
  }
};

// 案内区分の定義 (表示・切替で共用)
type InviteeFlagField = 'request_first_mail' | 'request_bulk_mail' | 'self_invite' | 'no_invite' | 'coop_regular_member';

const INVITEE_FLAG_DEFS: { field: InviteeFlagField; label: string; bg: string; color: string }[] = [
  { field: 'request_first_mail', label: '1次', bg: '#eff6ff', color: '#3b82f6' },
  { field: 'request_bulk_mail', label: '一斉', bg: '#ecfdf5', color: '#059669' },
  { field: 'self_invite', label: '自分で', bg: '#f5f3ff', color: '#8b5cf6' },
  { field: 'no_invite', label: '案内なし', bg: '#fee2e2', color: '#b91c1c' },
  { field: 'coop_regular_member', label: '協力会', bg: '#fffbeb', color: '#d97706' },
];

// 案内区分のチップ表示 (1行に該当区分をまとめて表示)
const inviteeFlagChips = (r: EventInviteeRow) => {
  const chips = INVITEE_FLAG_DEFS.filter((d) => !!r[d.field]);
  if (chips.length === 0) return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>-</Typography>;
  return (
    <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
      {chips.map((c) => (
        <Chip key={c.label} size="small" label={c.label} sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: c.bg, color: c.color }} />
      ))}
    </Box>
  );
};

// 案内区分メニューでの表示名 (チップの短縮名に対する正式名)
const INVITEE_FLAG_MENU_LABELS: Record<InviteeFlagField, string> = {
  request_first_mail: '1次案内希望（8月配信）',
  request_bulk_mail: '一斉メール希望（9月配信）',
  self_invite: '自分で案内',
  no_invite: '今回は案内なし',
  coop_regular_member: '協力会正会員',
};

// 案内区分セル。表示は ON の区分のみ (行高を1行に保つ)。
// 編集権限があればセルクリックでチェックボックスメニューを開き ON/OFF を切り替える
// (1次+一斉の併記など複数区分が実在するため単一選択にはしない)
const InviteeFlagsCell = React.memo(function InviteeFlagsCell({
  row,
  canEdit,
  onToggle,
}: {
  row: EventInviteeRow;
  canEdit: boolean;
  onToggle: (row: EventInviteeRow, field: InviteeFlagField) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!canEdit) return inviteeFlagChips(row);
  return (
    <>
      <Box
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          minHeight: 24,
          cursor: 'pointer',
          borderRadius: '6px',
          px: 0.5,
          py: 0.25,
          '&:hover': { bgcolor: '#eff6ff', '& .edit-hint': { opacity: 1 } },
        }}
      >
        {inviteeFlagChips(row)}
        <Icons.Edit className="edit-hint" sx={{ fontSize: '13px', color: '#94a3b8', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {INVITEE_FLAG_DEFS.map((d) => (
          <MenuItem key={d.field} dense sx={{ fontSize: '13px' }} onClick={() => onToggle(row, d.field)}>
            <Checkbox size="small" checked={!!row[d.field]} sx={{ p: 0.5, mr: 1 }} />
            {INVITEE_FLAG_MENU_LABELS[d.field]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

// 配信予定の状態 (plan_status) セル。クリックでメニューから選択する。
// 「案内送付済」はサーバー側で invited_at を初回自動記録、未設定へ戻すとクリアされる
const INVITEE_STATUS_OPTIONS = ['案内送付済', '参加予定', '不参加予定'];

const InviteeStatusCell = React.memo(function InviteeStatusCell({
  row,
  canEdit,
  onSelect,
}: {
  row: EventInviteeRow;
  canEdit: boolean;
  onSelect: (row: EventInviteeRow, status: string | null) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const chip = (
    <Chip
      size="small"
      label={row.plan_status || '未設定'}
      clickable={canEdit}
      onClick={canEdit ? (e) => setAnchor(e.currentTarget) : undefined}
      sx={{
        fontSize: '11px',
        height: '20px',
        fontWeight: 700,
        ...(row.plan_status === '案内送付済'
          ? { bgcolor: '#ecfdf5', color: '#059669' }
          : row.plan_status
            ? { bgcolor: '#eff6ff', color: '#3b82f6' }
            : { bgcolor: '#f1f5f9', color: '#94a3b8' }),
      }}
    />
  );
  if (!canEdit) return chip;
  return (
    <>
      {chip}
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
        {['未設定', ...INVITEE_STATUS_OPTIONS].map((s) => (
          <MenuItem
            key={s}
            selected={(row.plan_status || '未設定') === s}
            sx={{ fontSize: '13px' }}
            onClick={() => {
              setAnchor(null);
              const next = s === '未設定' ? null : s;
              if (next !== (row.plan_status || null)) onSelect(row, next);
            }}
          >
            {s}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
});

// テキスト項目 (送付先・備考) のインライン編集セル。
// クリックで TextField 展開、Enter/フォーカスアウトで確定、Escape でキャンセル
const InlineTextCell = React.memo(function InlineTextCell({
  value,
  canEdit,
  width,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  width: number;
  onSave: (next: string | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const committingRef = useRef(false);

  const startEdit = () => {
    if (!canEdit || saving) return;
    setDraft(value ?? '');
    committingRef.current = false;
    setEditing(true);
  };

  const commit = async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    setEditing(false);
    const next = draft.trim() === '' ? null : draft.trim();
    if (next === (value ?? null)) return;
    setSaving(true);
    await onSave(next);
    setSaving(false);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            committingRef.current = true;
            setEditing(false);
          }
        }}
        sx={{ width, '& .MuiInputBase-input': { fontSize: '13px', py: 0.5 } }}
      />
    );
  }
  return (
    <Box
      onClick={startEdit}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 24,
        maxWidth: width,
        ...(canEdit && {
          cursor: 'pointer',
          borderRadius: '6px',
          px: 0.75,
          py: 0.25,
          '&:hover': { bgcolor: '#eff6ff', '& .edit-hint': { opacity: 1 } },
        }),
      }}
    >
      {saving ? (
        <CircularProgress size={14} />
      ) : (
        <>
          <Typography sx={{ fontSize: '13px', color: value ? 'inherit' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value ?? (canEdit ? '未入力' : '')}
          </Typography>
          {canEdit && (
            <Icons.Edit className="edit-hint" sx={{ fontSize: '13px', color: '#94a3b8', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
          )}
        </>
      )}
    </Box>
  );
});

// 日付セルのインライン編集 (クリック → type=date 入力 → blur/Enter 確定)。
// InlineTextCell と同じ操作感で、値は 'YYYY-MM-DD' 文字列 (クリアで null)
const InlineDateCell = React.memo(function InlineDateCell({
  value,
  canEdit,
  width,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  width: number;
  onSave: (next: string | null) => Promise<boolean>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const committingRef = useRef(false);

  const startEdit = () => {
    if (!canEdit || saving) return;
    setDraft(value ?? '');
    committingRef.current = false;
    setEditing(true);
  };

  const commit = async () => {
    if (committingRef.current) return;
    committingRef.current = true;
    setEditing(false);
    const next = draft === '' ? null : draft;
    if (next === (value ?? null)) return;
    setSaving(true);
    await onSave(next);
    setSaving(false);
  };

  if (editing) {
    return (
      <TextField
        size="small"
        type="date"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            committingRef.current = true;
            setEditing(false);
          }
        }}
        sx={{ width, '& .MuiInputBase-input': { fontSize: '13px', py: 0.5 } }}
      />
    );
  }
  return (
    <Box
      onClick={startEdit}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 24,
        maxWidth: width,
        ...(canEdit && {
          cursor: 'pointer',
          borderRadius: '6px',
          px: 0.75,
          py: 0.25,
          '&:hover': { bgcolor: '#eff6ff', '& .edit-hint': { opacity: 1 } },
        }),
      }}
    >
      {saving ? (
        <CircularProgress size={14} />
      ) : (
        <>
          <Typography sx={{ fontSize: '13px', color: value ? 'inherit' : '#94a3b8', whiteSpace: 'nowrap' }}>
            {value ?? (canEdit ? '未入力' : '')}
          </Typography>
          {canEdit && (
            <Icons.Edit className="edit-hint" sx={{ fontSize: '13px', color: '#94a3b8', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
          )}
        </>
      )}
    </Box>
  );
});

// 従業員参照セルのインライン編集 (クリック → スタッフ Autocomplete → 選択で確定)。
// 値は氏名文字列で保存する (協賛品の受領者など、マスタ参照だが緩い紐づけで良い列向け)
const InlineStaffCell = React.memo(function InlineStaffCell({
  value,
  canEdit,
  width,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  width: number;
  onSave: (next: string | null) => Promise<boolean>;
}) {
  const { staffList, loading: masterLoading } = useMasterData();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const commit = async (next: string | null) => {
    setEditing(false);
    if (next === (value ?? null)) return;
    setSaving(true);
    await onSave(next);
    setSaving(false);
  };

  if (editing) {
    return (
      <Autocomplete
        options={staffList}
        size="small"
        openOnFocus
        loading={masterLoading}
        noOptionsText={masterLoading ? '読み込み中...' : '該当なし'}
        getOptionLabel={(option: any) => option.staff_name || ''}
        value={staffList.find((s: any) => s.staff_name === value) || null}
        onChange={(_, newValue: any) => commit(newValue ? newValue.staff_name : null)}
        isOptionEqualToValue={(option: any, v: any) => option.staff_id === v.staff_id}
        renderOption={(props, option: any) => (
          <li {...props} key={option.staff_id}>
            {option.staff_name}（{option.branch_name}）
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            placeholder="氏名で検索"
            onBlur={() => setEditing(false)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setEditing(false);
            }}
            sx={{ '& .MuiInputBase-input': { fontSize: '13px' } }}
          />
        )}
        sx={{ width: width + 40 }}
      />
    );
  }
  return (
    <Box
      onClick={() => canEdit && !saving && setEditing(true)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        minHeight: 24,
        maxWidth: width,
        ...(canEdit && {
          cursor: 'pointer',
          borderRadius: '6px',
          px: 0.75,
          py: 0.25,
          '&:hover': { bgcolor: '#eff6ff', '& .edit-hint': { opacity: 1 } },
        }),
      }}
    >
      {saving ? (
        <CircularProgress size={14} />
      ) : (
        <>
          <Typography sx={{ fontSize: '13px', color: value ? 'inherit' : '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {value ?? (canEdit ? '未選択' : '')}
          </Typography>
          {canEdit && (
            <Icons.Edit className="edit-hint" sx={{ fontSize: '13px', color: '#94a3b8', opacity: 0, transition: 'opacity 0.15s', flexShrink: 0 }} />
          )}
        </>
      )}
    </Box>
  );
});

// 配信予定への案内先追加ダイアログ。CRM 顧客 (個人・法人) を横断検索して1件追加する
const AddInviteeDialog = React.memo(function AddInviteeDialog({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (customerId: string) => Promise<boolean>;
}) {
  type CustomerOption = { id: string; customer_type: string; display_name: string; employer_name?: string | null };
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [selected, setSelected] = useState<CustomerOption | null>(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seqRef = useRef(0);

  const handleSearch = (query: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query.trim()) {
      setOptions([]);
      return;
    }
    setSearching(true);
    const seq = ++seqRef.current;
    timerRef.current = setTimeout(async () => {
      try {
        const res = await axios.post(ep('search_crm_customers'), { query });
        if (seq !== seqRef.current) return;
        setOptions(res.data.status === 'success' ? res.data.results || [] : []);
      } catch {
        if (seq === seqRef.current) setOptions([]);
      } finally {
        if (seq === seqRef.current) setSearching(false);
      }
    }, 300);
  };

  const handleAdd = async () => {
    if (!selected || saving) return;
    setSaving(true);
    const ok = await onAdd(selected.id);
    setSaving(false);
    if (ok) {
      setSelected(null);
      setOptions([]);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icons.PersonAdd sx={{ fontSize: 18 }} /> 案内先を追加
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        <Typography sx={{ fontSize: '12px', color: '#64748b', mb: 1.5 }}>
          顧客管理システムの個人・法人を検索して配信予定へ追加します。個人は勤務先法人も自動で紐づきます。
        </Typography>
        <Autocomplete
          options={options}
          loading={searching}
          value={selected}
          filterOptions={(x) => x}
          getOptionLabel={(o) => (o.employer_name ? `${o.display_name}（${o.employer_name}）` : o.display_name || '')}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          noOptionsText="氏名・会社名で検索してください"
          onInputChange={(_, v, reason) => {
            if (reason === 'input') handleSearch(v);
          }}
          onChange={(_, v) => setSelected(v)}
          renderOption={(props, o) => (
            <li {...props} key={o.id}>
              <Chip
                size="small"
                label={o.customer_type === 'individual' ? '個人' : '法人'}
                sx={{
                  fontSize: '11px',
                  height: '20px',
                  fontWeight: 700,
                  mr: 1,
                  ...(o.customer_type === 'individual'
                    ? { bgcolor: '#eff6ff', color: '#3b82f6' }
                    : { bgcolor: '#f5f3ff', color: '#8b5cf6' }),
                }}
              />
              <Typography sx={{ fontSize: '13px' }}>{o.display_name}</Typography>
              {o.employer_name && (
                <Typography sx={{ fontSize: '12px', color: '#94a3b8', ml: 0.75 }}>（{o.employer_name}）</Typography>
              )}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} size="small" autoFocus placeholder="氏名・会社名で検索" />
          )}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>
          キャンセル
        </Button>
        <Button onClick={handleAdd} disabled={!selected || saving} startIcon={<Icons.PersonAdd />} sx={STANDARD_CTA_BUTTON_SX}>
          追加する
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default function EventManagement() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const {
    event,
    entries,
    participants,
    payments,
    sponsorships,
    invitees,
    resultFormat,
    results,
    teamResults,
    awards,
    loading,
    error,
    summary,
    reload,
    updatePayment,
    updateSponsorship,
    updateEventMail,
    updateInvitee,
    addInvitee,
    deleteInvitee,
    requestDeleteInvitee,
    sendInviteeMail,
    saveAssignments,
    addStaffParticipants,
    removeStaffParticipant,
    updateParticipant,
    addParticipant,
    cancelParticipant,
    cancelEntry,
    saveAward,
    deleteAward,
    createAwardSlots,
    reorderAwards,
    uploadAwardImage,
    deleteAwardImage,
  } = useEventManagement(Number(eventId));

  const { user } = useAuth();
  const { permissionData } = usePermissions();
  const confirm = useConfirm();
  const notify = useNotification();
  // 入金消込 (入金額のインライン編集・状況チップの手動切替) は管理者のみ
  const canEditPayment = permissionData.includes('event.master.edit');

  const handleSavePaidAmount = useCallback(
    async (paymentId: number, paidAmount: number | null): Promise<boolean> => {
      const r = await updatePayment(paymentId, { paid_amount: paidAmount });
      if (r.ok) notify.success('入金額を更新しました');
      else notify.error(r.error || '入金額の更新に失敗しました');
      return r.ok;
    },
    [updatePayment, notify],
  );

  // 入金一覧の備考 (督促メモ等) のインライン保存。編集は管理者のみ
  const handleSavePaymentNote = useCallback(
    async (paymentId: number, note: string | null): Promise<boolean> => {
      const r = await updatePayment(paymentId, { note });
      if (r.ok) notify.success('備考を保存しました');
      else notify.error(r.error || '備考の保存に失敗しました');
      return r.ok;
    },
    [updatePayment, notify],
  );

  // 入金日のインライン修正 (通常は入金額の消込時に自動記録される)。編集は管理者のみ
  const handleSavePaidDate = useCallback(
    async (paymentId: number, paidDate: string | null): Promise<boolean> => {
      const r = await updatePayment(paymentId, { paid_date: paidDate });
      if (r.ok) notify.success('入金日を保存しました');
      else notify.error(r.error || '入金日の保存に失敗しました');
      return r.ok;
    },
    [updatePayment, notify],
  );

  const handleTogglePaid = useCallback(
    async (row: EventPaymentRow) => {
      const next = !row.is_paid;
      const ok = await confirm({
        title: '入金状況の変更',
        message: `受付NO ${row.reception_no}（${row.company_name}）を「${next ? '入金済' : '未入金'}」に変更しますか？`,
        confirmText: '変更する',
      });
      if (!ok) return;
      const r = await updatePayment(row.id, { is_paid: next });
      if (r.ok) notify.success('入金状況を変更しました');
      else notify.error(r.error || '入金状況の変更に失敗しました');
    },
    [updatePayment, confirm, notify],
  );

  // 配信予定の編集 (フラグ切替・状態・送付先/備考・追加/除外) も event.master.edit で統一
  const canEditInvitee = canEditPayment;
  // 個別送信・削除依頼の権限 (全員展開用の専用キー。管理者は常に可)
  const canSendMail = canEditInvitee || permissionData.includes('event.mail.send');

  // 協賛品の運営入力 (商品名・点数・持参日程・保管場所・受領・御礼状) は全社員が編集できる
  const handleSaveSponsorshipField = useCallback(
    async (
      sponsorshipId: number,
      patch: Parameters<typeof updateSponsorship>[1],
    ): Promise<boolean> => {
      const r = await updateSponsorship(sponsorshipId, patch);
      if (r.ok) notify.success('協賛品情報を保存しました');
      else notify.error(r.error || '協賛品情報の保存に失敗しました');
      return r.ok;
    },
    [updateSponsorship, notify],
  );

  const handleSaveSponsorshipQuantity = useCallback(
    async (sponsorshipId: number, value: string | null): Promise<boolean> => {
      if (value !== null && !/^\d+$/.test(value)) {
        notify.error('点数は 0 以上の数値で入力してください');
        return false;
      }
      return handleSaveSponsorshipField(sponsorshipId, { quantity: value === null ? null : parseInt(value, 10) });
    },
    [handleSaveSponsorshipField, notify],
  );

  const handleToggleSponsorshipFlag = useCallback(
    async (row: EventSponsorshipRow, field: 'is_received' | 'thankyou_sent') => {
      const label = field === 'is_received' ? '受領' : '御礼状';
      const next = !row[field];
      const ok = await confirm({
        title: `${label}の変更`,
        message: `受付NO ${row.reception_no}（${row.company_name}）の${label}を「${next ? '済' : '未'}」に変更しますか？`,
        confirmText: '変更する',
      });
      if (!ok) return;
      const r = await updateSponsorship(row.id, { [field]: next });
      if (r.ok) notify.success(`${label}を変更しました`);
      else notify.error(r.error || `${label}の変更に失敗しました`);
    },
    [updateSponsorship, confirm, notify],
  );

  const handleToggleInviteeFlag = useCallback(
    async (row: EventInviteeRow, field: InviteeFlagField) => {
      const r = await updateInvitee(row.id, { [field]: !row[field] });
      if (!r.ok) notify.error(r.error || '案内区分の変更に失敗しました');
    },
    [updateInvitee, notify],
  );

  const handleSetInviteeStatus = useCallback(
    async (row: EventInviteeRow, status: string | null) => {
      const r = await updateInvitee(row.id, { plan_status: status });
      if (r.ok) notify.success('状態を更新しました');
      else notify.error(r.error || '状態の更新に失敗しました');
    },
    [updateInvitee, notify],
  );

  const handleSaveInviteeText = useCallback(
    async (inviteeId: number, field: 'note', value: string | null): Promise<boolean> => {
      const r = await updateInvitee(inviteeId, { [field]: value });
      if (r.ok) notify.success('保存しました');
      else notify.error(r.error || '保存に失敗しました');
      return r.ok;
    },
    [updateInvitee, notify],
  );

  const handleDeleteInvitee = useCallback(
    async (row: EventInviteeRow) => {
      const target = row.person_name || row.company_name || `id=${row.id}`;
      const ok = await confirm({
        title: '配信予定から除外',
        message: `「${target}」を配信予定から除外しますか？`,
        confirmText: '除外する',
        destructive: true,
      });
      if (!ok) return;
      const r = await deleteInvitee(row.id);
      if (r.ok) notify.success('配信予定から除外しました');
      else notify.error(r.error || '除外に失敗しました');
    },
    [deleteInvitee, confirm, notify],
  );

  // 削除依頼 (一般ユーザー向け)。直接削除せず実行委員会の Chat へ通知する
  const handleRequestDeleteInvitee = useCallback(
    async (row: EventInviteeRow) => {
      if (!user?.email) return;
      const target = row.person_name || row.company_name || `id=${row.id}`;
      const ok = await confirm({
        title: '削除依頼の通知',
        message: `「${target}」の配信予定削除を実行委員会の Chat へ依頼しますか？（この場では削除されません）`,
        confirmText: '依頼する',
      });
      if (!ok) return;
      const r = await requestDeleteInvitee(row.id, user.email);
      if (r.ok) notify.success('実行委員会へ削除依頼を通知しました');
      else notify.error(r.error || '削除依頼の通知に失敗しました');
    },
    [requestDeleteInvitee, user?.email, confirm, notify],
  );

  const [addInviteeOpen, setAddInviteeOpen] = useState(false);
  // 配信予定行・参加者行からの顧客情報編集ダイアログ (個人行は個人顧客、法人宛行は法人顧客を開く)
  const [editCustomerId, setEditCustomerId] = useState<string | null>(null);

  // 参加者の編集・お連れ様の追加ダイアログ (event.master.edit 保有者のみ)
  const [participantDialog, setParticipantDialog] = useState<{
    mode: 'edit' | 'add';
    entry: EventParticipantEntry;
    participant?: EventParticipantRow;
  } | null>(null);

  const openParticipantDialog = useCallback((mode: 'edit' | 'add', row: EventParticipantRow) => {
    setParticipantDialog({
      mode,
      entry: { entry_id: row.entry_id, reception_no: row.reception_no, company_name: row.company_name, hozenall_user_id: row.hozenall_user_id },
      participant: mode === 'edit' ? row : undefined,
    });
  }, []);

  const handleSaveParticipant = useCallback(
    async (input: EventParticipantInput): Promise<boolean> => {
      if (!participantDialog) return false;
      if (participantDialog.mode === 'edit' && participantDialog.participant) {
        const r = await updateParticipant(participantDialog.participant.id, input, user?.email ?? undefined);
        if (r.ok) notify.success('参加者を更新しました');
        else notify.error(r.error || '参加者の更新に失敗しました');
        return r.ok;
      }
      const r = await addParticipant(participantDialog.entry.entry_id, input, user?.email ?? undefined);
      if (r.ok) notify.success(`お連れ様を追加しました${r.expectedAmount != null ? `（入金予定額 ${yen(r.expectedAmount)}）` : ''}`);
      else notify.error(r.error || 'お連れ様の追加に失敗しました');
      return r.ok;
    },
    [participantDialog, updateParticipant, addParticipant, user?.email, notify],
  );

  // お連れ様の取消 (参加者行と配置を削除し、申込の人数と入金予定額を付け直す)
  const handleCancelParticipant = useCallback(
    async (row: EventParticipantRow) => {
      const name = `${row.last_name ?? ''} ${row.first_name ?? ''}`.trim() || `id=${row.id}`;
      const ok = await confirm({
        title: 'お連れ様の取消',
        message: `受付NO ${row.reception_no}（${row.company_name}）の ${name} 様の参加を取り消しますか？参加者一覧と組割から外れ、申込の人数と入金予定額を付け直します。`,
        confirmText: '取り消す',
        destructive: true,
      });
      if (!ok) return;
      const r = await cancelParticipant(row.id, user?.email ?? undefined);
      if (r.ok) notify.success(`${name} 様の参加を取り消しました${r.expectedAmount != null ? `（入金予定額 ${yen(r.expectedAmount)}）` : ''}`);
      else notify.error(r.error || 'お連れ様の取消に失敗しました');
    },
    [cancelParticipant, confirm, notify, user?.email],
  );

  // 申込ごとの参加者数・申込者名・社員の申込か (申込の取消の確認文と操作の出し分けに使う)
  const entryMembers = useMemo(() => {
    const m = new Map<number, { count: number; applicant: string; staff: boolean }>();
    for (const p of participants) {
      const cur = m.get(p.entry_id) ?? { count: 0, applicant: '', staff: false };
      cur.count += 1;
      if (p.branch_no === '00') cur.applicant = `${p.last_name ?? ''} ${p.first_name ?? ''}`.trim();
      if (p.is_staff) cur.staff = true;
      m.set(p.entry_id, cur);
    }
    return m;
  }, [participants]);

  // 申込の取消 (欠席へ切替)。参加者・配置を削除し、入金の記録があれば返金確認のため入金一覧に残す
  const handleCancelEntry = useCallback(
    async (entryId: number, receptionNo: string, companyName: string | null) => {
      const members = entryMembers.get(entryId);
      const who =
        members && members.count > 1
          ? `参加者 ${members.count} 名全員`
          : `${members?.applicant || '申込者'} 様`;
      const ok = await confirm({
        title: '申込の取消',
        message: `受付NO ${receptionNo}（${companyName ?? ''}）の申込を取り消して欠席にしますか？${who}が参加者一覧と組割から外れます。入金の記録がある場合は返金の確認のため入金一覧に残し、入金予定額を 0 円にします。`,
        confirmText: '取り消す',
        destructive: true,
      });
      if (!ok) return;
      const r = await cancelEntry(entryId, user?.email ?? undefined);
      if (!r.ok) {
        notify.error(r.error || '申込の取消に失敗しました');
        return;
      }
      if (r.paymentKept) {
        notify.warning(
          `受付NO ${receptionNo} の申込を取り消しました。入金${r.paidAmount != null ? ` ${yen(r.paidAmount)}` : ''}の記録があるため入金一覧に残しています。返金の要否を確認してください`,
        );
      } else {
        notify.success(`受付NO ${receptionNo} の申込を取り消しました（欠席）`);
      }
    },
    [entryMembers, cancelEntry, confirm, notify, user?.email],
  );

  // 賞品の授与の編集 (追加・編集・削除・受け取り・発表順・協賛品からの賞品枠) も event.master.edit で統一
  const canEditAward = canEditInvitee;
  const [awardDialog, setAwardDialog] = useState<{ mode: 'add' | 'edit'; award?: EventAwardRow } | null>(null);
  const [awardSlotsOpen, setAwardSlotsOpen] = useState(false);
  const [awardOrderOpen, setAwardOrderOpen] = useState(false);
  // 一覧のサムネイルから開く賞品の画像の拡大表示
  const [awardImagePreview, setAwardImagePreview] = useState<EventAwardRow | null>(null);

  // 行 (input が null なら項目は変えていない) を保存してから画像を付ける・外す。
  // 行は保存できて画像だけ失敗したときは、行の追加を二重にしないようダイアログを閉じて案内する
  const handleSaveAward = useCallback(
    async (input: EventAwardInput | null, image: AwardImageChange): Promise<boolean> => {
      if (!awardDialog) return false;
      const operator = user?.email ?? undefined;
      let awardId = awardDialog.award?.id ?? null;
      if (input) {
        const r = await saveAward(awardId, input, operator);
        if (!r.ok) {
          notify.error(r.error || '賞品の授与の保存に失敗しました');
          return false;
        }
        awardId = r.awardId ?? awardId;
      }
      if (image !== 'keep' && awardId != null) {
        const ri = image === 'remove' ? await deleteAwardImage(awardId, operator) : await uploadAwardImage(awardId, image, operator);
        if (!ri.ok) {
          notify.error(`${input ? '賞品は保存しましたが、' : ''}${ri.error || '賞品の画像を保存できませんでした'}`);
          return !!input;
        }
      }
      notify.success(awardDialog.mode === 'edit' ? '賞品の授与を更新しました' : '賞品を追加しました');
      return true;
    },
    [awardDialog, saveAward, uploadAwardImage, deleteAwardImage, user?.email, notify],
  );

  const handleReorderAwards = useCallback(
    async (awardIds: number[]): Promise<boolean> => {
      const r = await reorderAwards(awardIds, user?.email ?? undefined);
      if (r.ok) notify.success('発表順を並べ替えました');
      else notify.error(r.error || '発表順の並べ替えに失敗しました');
      return r.ok;
    },
    [reorderAwards, notify, user?.email],
  );

  const handleDeleteAward = useCallback(
    async (row: EventAwardRow) => {
      const winner = row.participant_id != null ? `（${personName(row)} 様）` : '（当選者未定）';
      const ok = await confirm({
        title: '賞品の授与の削除',
        message: `「${row.award_name}　${row.prize_name ?? ''}」${winner}を削除しますか？協賛品の割当も外れます。`,
        confirmText: '削除する',
        destructive: true,
      });
      if (!ok) return;
      const r = await deleteAward(row.id, user?.email ?? undefined);
      if (r.ok) notify.success('賞品の授与を削除しました');
      else notify.error(r.error || '賞品の授与の削除に失敗しました');
    },
    [deleteAward, confirm, notify, user?.email],
  );

  const handleToggleAwardReceived = useCallback(
    async (row: EventAwardRow) => {
      if (row.participant_id == null) {
        notify.info('当選者が未定のため、受け取りは切り替えられません');
        return;
      }
      const next = !row.is_received;
      const ok = await confirm({
        title: '受け取りの変更',
        message: `${personName(row)} 様の「${row.award_name}」の受け取りを「${next ? '済' : '未'}」に変更しますか？`,
        confirmText: '変更する',
      });
      if (!ok) return;
      const r = await saveAward(row.id, { is_received: next }, user?.email ?? undefined);
      if (r.ok) notify.success('受け取りを変更しました');
      else notify.error(r.error || '受け取りの変更に失敗しました');
    },
    [saveAward, confirm, notify, user?.email],
  );

  const handleSaveAwardOrder = useCallback(
    async (awardId: number, value: string | null): Promise<boolean> => {
      if (value !== null && !/^\d+$/.test(value)) {
        notify.error('発表順は 0 以上の数値で入力してください');
        return false;
      }
      const r = await saveAward(awardId, { sort_order: value === null ? null : parseInt(value, 10) }, user?.email ?? undefined);
      if (!r.ok) notify.error(r.error || '発表順の保存に失敗しました');
      return r.ok;
    },
    [saveAward, notify, user?.email],
  );

  const handleCreateAwardSlots = useCallback(
    async (sponsorshipIds: number[], awardType: '表彰' | '抽選', awardName: string): Promise<boolean> => {
      const r = await createAwardSlots(sponsorshipIds, awardType, awardName, user?.email ?? undefined);
      if (r.ok) notify.success(`賞品枠を ${r.created ?? 0} 件作成しました`);
      else notify.error(r.error || '賞品枠の作成に失敗しました');
      return r.ok;
    },
    [createAwardSlots, notify, user?.email],
  );

  // 案内メールの個別送信 (操作者本人名義)。送付済み行は再送確認を挟む
  const [sendingInviteeId, setSendingInviteeId] = useState<number | null>(null);
  const handleSendInviteeMail = useCallback(
    async (row: EventInviteeRow) => {
      if (!user?.email) return;
      const target = [row.company_name, row.person_name].filter(Boolean).join(' ') || `id=${row.id}`;
      const toAddr = row.business_email || row.company_email;
      const resend = row.plan_status === '案内送付済';
      const ok = await confirm({
        title: resend ? '案内メールの再送' : '案内メールの送信',
        message: resend
          ? `「${target}」は送付済みです。${toAddr} 宛にもう一度送信しますか？（忘年会実行委員会 soumu@ 名義・控えが自分宛に届きます）`
          : `「${target}」（${toAddr}）へ案内メールを送信しますか？（忘年会実行委員会 soumu@ 名義・控えが自分宛に届きます）`,
        confirmText: resend ? '再送する' : '送信する',
      });
      if (!ok) return;
      setSendingInviteeId(row.id);
      const r = await sendInviteeMail(row.id, user.email);
      setSendingInviteeId(null);
      if (r.ok) notify.success('案内メールを送信しました');
      else notify.error(r.error || '送信に失敗しました');
    },
    [user?.email, confirm, sendInviteeMail, notify],
  );

  const handleAddInvitee = useCallback(
    async (customerId: string): Promise<boolean> => {
      const r = await addInvitee(customerId);
      if (r.ok) notify.success('配信予定へ追加しました');
      else notify.error(r.error || '追加に失敗しました');
      return r.ok;
    },
    [addInvitee, notify],
  );

  const [tab, setTab] = useState<TabKey>('entries');
  const [filterText, setFilterText] = useState('');
  const [inviteeFilter, setInviteeFilter] = useState<InviteeFilterKey>('all');
  const [entryFilter, setEntryFilter] = useState<EntryFilterKey>('all');
  const [awardFilter, setAwardFilter] = useState<AwardFilterKey>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // ページング・タブ・絞り込みの切替時はテーブルのスクロールをトップへ戻す
  const tableScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (tableScrollRef.current) tableScrollRef.current.scrollTop = 0;
  }, [page, pageSize, tab, inviteeFilter, entryFilter, awardFilter, filterText]);

  const handleTabChange = (_: React.SyntheticEvent, next: TabKey) => {
    setTab(next);
    setPage(0);
  };

  const handleInviteeFilter = (key: InviteeFilterKey) => {
    setInviteeFilter(key);
    setPage(0);
  };

  const handleEntryFilter = (key: EntryFilterKey) => {
    setEntryFilter(key);
    setPage(0);
  };

  const handleAwardFilter = (key: AwardFilterKey) => {
    setAwardFilter(key);
    setPage(0);
  };

  const handleFilterCommit = useCallback((v: string) => {
    setFilterText(v.trim());
    setPage(0);
  }, []);

  // タブ別の検索対象テキスト連結 (部分一致・大文字小文字区別なし)
  const filtered = useMemo(() => {
    const q = filterText.toLowerCase();
    const match = (parts: (string | number | null | undefined)[]) =>
      q === '' || parts.some((p) => p != null && String(p).toLowerCase().includes(q));
    return {
      entries: entries.filter(
        (r) =>
          matchEntryFilter(r, entryFilter) &&
          match([r.reception_no, r.company_name, r.company_kana, r.hozenall_person, r.industry]),
      ),
      participants: participants.filter((r) =>
        match([r.reception_no, r.company_name, `${r.last_name}${r.first_name}`, `${r.last_kana}${r.first_kana}`, r.email, r.hozenall_person, r.customer_name]),
      ),
      payments: payments.filter((r) => match([r.reception_no, r.company_name, r.note, r.hozenall_person])),
      sponsorships: sponsorships.filter((r) => match([r.reception_no, r.company_name, r.product_name, r.staff, r.hozenall_person])),
      invitees: invitees.filter(
        (r) =>
          matchInviteeFilter(r, inviteeFilter) &&
          match([r.company_name, r.person_name, r.business_email, r.company_email, r.staff_name, r.note]),
      ),
      results: results.filter((r) => match([personName(r), r.company_name, r.table_no, r.note])),
      teamResults: teamResults.filter((r) => match([r.team_no, r.team_name, r.note])),
      awards: awards.filter(
        (r) =>
          matchAwardFilter(r, awardFilter) &&
          match([r.award_name, r.prize_name, personName(r), r.company_name, r.sponsor_company_name, r.table_no, r.note]),
      ),
    };
  }, [entries, participants, payments, sponsorships, invitees, results, teamResults, awards, filterText, inviteeFilter, entryFilter, awardFilter]);

  const entryColumns: Column[] = useMemo(
    () => [
      // 申込一覧は横スクロールが長いため、受付NO・出欠・会社名を1列へまとめて左端に固定する
      // (複数列を個別に固定すると境界ごとの再描画でスクロールがカクつくため1列に統合)
      // 見出しと値は ENTRY_STICKY_GRID を共有して桁を揃える
      {
        field: 'reception_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: ENTRY_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">受付NO</Box>
            <Box component="span">出欠</Box>
            <Box component="span">会社名 お客様名</Box>
          </Box>
        ),
        width: 340,
        sticky: true,
        renderCell: (r: EventEntryRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: ENTRY_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>{r.reception_no}</Typography>
            <Box sx={{ display: 'flex' }}>{attendanceChip(r.attendance)}</Box>
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.company_name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'invitee_company_name',
        headerName: '配信元',
        width: 200,
        renderCell: (r: EventEntryRow) => {
          if (!r.invitee_id) return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>-</Typography>;
          const label =
            [r.invitee_company_name, r.invitee_person_name].filter(Boolean).join(' ') || '配信予定';
          return (
            <Tooltip title="案内メールの申込URL経由 (配信予定の顧客と紐づけ済み)" arrow>
              <Chip
                size="small"
                label={label}
                sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669', maxWidth: '100%' }}
              />
            </Tooltip>
          );
        },
      },
      { field: 'entry_type', headerName: '区分', width: 60 },
      { field: 'participants_count', headerName: '人数', width: 60, align: 'right' },
      {
        field: 'industry',
        headerName: '業種',
        width: 160,
        renderCell: (r: EventEntryRow) => (r.industry_other ? `${r.industry}（${r.industry_other}）` : r.industry),
      },
      {
        field: 'hozenall_person',
        headerName: '当社担当',
        width: 140,
        renderCell: (r: EventEntryRow) =>
          r.hozenall_person ? `${r.hozenall_person}${r.hozenall_branch ? `（${r.hozenall_branch}）` : ''}` : '',
      },
      { field: 'sponsorship', headerName: '協賛', width: 60, align: 'center', renderCell: (r: EventEntryRow) => circle(r.sponsorship) },
      { field: 'is_paid', headerName: '入金', width: 80, align: 'center', renderCell: (r: EventEntryRow) => paidChip(r.is_paid) },
      {
        field: 'mail_sent',
        headerName: '案内メール',
        width: 90,
        align: 'center',
        renderCell: (r: EventEntryRow) => {
          if (r.attendance !== '出席') return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>-</Typography>;
          return r.mail_sent ? (
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>送信済</Typography>
          ) : (
            <Chip size="small" label="未送" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#fee2e2', color: '#b91c1c' }} />
          );
        },
      },
      { field: 'entry_date', headerName: '申込日', width: 90 },
      { field: 'absence_reason', headerName: '欠席アンケート', width: 160 },
      ...(canEditPayment
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 60,
              align: 'center' as const,
              // 出席の申込だけ取消できる (社員の申込は組割の「社員の除外」で扱う)
              renderCell: (r: EventEntryRow) =>
                r.attendance === '出席' && !entryMembers.get(r.id)?.staff ? (
                  <Tooltip title="申込を取消（欠席にする）" arrow>
                    <IconButton size="small" onClick={() => handleCancelEntry(r.id, r.reception_no, r.company_name)}>
                      <Icons.Cancel sx={{ fontSize: '16px', color: '#94a3b8' }} />
                    </IconButton>
                  </Tooltip>
                ) : null,
            },
          ]
        : []),
    ],
    [canEditPayment, entryMembers, handleCancelEntry],
  );

  const participantColumns: Column[] = useMemo(
    () => [
      // 申込一覧と同様、受付NO・会社名を1列へまとめて左端に固定する
      // (複数列を個別に固定すると境界ごとの再描画でスクロールがカクつくため1列に統合)
      {
        field: 'reception_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">受付NO</Box>
            <Box component="span">会社名 お客様名</Box>
          </Box>
        ),
        width: 300,
        sticky: true,
        renderCell: (r: EventParticipantRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>{r.reception_no}</Typography>
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.company_name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'branch_no',
        headerName: '種別',
        width: 80,
        renderCell: (r: EventParticipantRow) =>
          r.branch_no === '00' ? (
            <Chip size="small" label="申込者" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#eff6ff', color: '#3b82f6' }} />
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#64748b' }}>お連れ様</Typography>
          ),
      },
      { field: 'last_name', headerName: '氏名', width: 130, renderCell: (r: EventParticipantRow) => `${r.last_name ?? ''} ${r.first_name ?? ''}` },
      { field: 'last_kana', headerName: 'フリガナ', width: 150, renderCell: (r: EventParticipantRow) => `${r.last_kana ?? ''} ${r.first_kana ?? ''}` },
      { field: 'position', headerName: '役職', width: 120 },
      { field: 'email', headerName: 'メールアドレス', width: 220 },
      {
        field: 'customer_name',
        headerName: '顧客管理',
        width: 170,
        // 紐付いた個人顧客 (クリックで顧客情報を開く)。社員は社員マスタと紐付くため対象外
        renderCell: (r: EventParticipantRow) => {
          if (r.is_staff) return <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>社員</Typography>;
          if (!r.customer_id) {
            return (
              <Chip size="small" label="未紐付け" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#fef3c7', color: '#92400e' }} />
            );
          }
          return (
            <Tooltip title={r.customer_employer_name ? `勤務先 ${r.customer_employer_name}（クリックで顧客情報）` : 'クリックで顧客情報'} arrow>
              <Chip
                size="small"
                clickable
                onClick={() => setEditCustomerId(r.customer_id)}
                label={r.customer_name || '紐付け済み'}
                sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669', maxWidth: '100%' }}
              />
            </Tooltip>
          );
        },
      },
      {
        field: 'hozenall_person',
        headerName: '当社担当',
        width: 140,
        // 申込フォームで入力された当社担当者 (申込一覧と同じ表記)
        renderCell: (r: EventParticipantRow) =>
          r.hozenall_person ? `${r.hozenall_person}${r.hozenall_branch ? `（${r.hozenall_branch}）` : ''}` : '',
      },
      ...(canEditPayment
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 110,
              align: 'center' as const,
              // 社員の行は組割の「社員を追加・除外」で扱うため操作を出さない
              renderCell: (r: EventParticipantRow) =>
                r.is_staff ? null : (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Tooltip title="参加者を編集・顧客と紐付け" arrow>
                      <IconButton size="small" onClick={() => openParticipantDialog('edit', r)}>
                        <Icons.Edit sx={{ fontSize: '16px', color: '#94a3b8' }} />
                      </IconButton>
                    </Tooltip>
                    {r.branch_no === '00' && r.attendance === '出席' && (
                      <Tooltip title="この申込にお連れ様を追加" arrow>
                        <IconButton size="small" onClick={() => openParticipantDialog('add', r)}>
                          <Icons.PersonAdd sx={{ fontSize: '16px', color: '#3b82f6' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {r.branch_no !== '00' && (
                      <Tooltip title="お連れ様の参加を取消" arrow>
                        <IconButton size="small" onClick={() => handleCancelParticipant(r)}>
                          <Icons.PersonRemove sx={{ fontSize: '16px', color: '#94a3b8' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* 1 名だけの申込は申込者の取消 = 申込の取消 (お連れ様がいる申込は申込一覧から全員分を取り消す) */}
                    {r.branch_no === '00' && r.attendance === '出席' && entryMembers.get(r.entry_id)?.count === 1 && (
                      <Tooltip title="申込を取消（欠席にする）" arrow>
                        <IconButton size="small" onClick={() => handleCancelEntry(r.entry_id, r.reception_no, r.company_name)}>
                          <Icons.PersonRemove sx={{ fontSize: '16px', color: '#94a3b8' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                ),
            },
          ]
        : []),
    ],
    [canEditPayment, openParticipantDialog, handleCancelParticipant, entryMembers, handleCancelEntry],
  );

  const paymentColumns: Column[] = useMemo(
    () => [
      // 受付NO・会社名を1列へまとめて左端に固定する (他タブと同一方式)
      {
        field: 'reception_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">受付NO</Box>
            <Box component="span">会社名 お客様名</Box>
          </Box>
        ),
        width: 300,
        sticky: true,
        renderCell: (r: EventPaymentRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>{r.reception_no}</Typography>
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.company_name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'hozenall_person',
        headerName: '当社担当',
        width: 140,
        // 申込フォームで入力された当社担当者 (督促の担当割り確認用)
        renderCell: (r: EventPaymentRow) =>
          r.hozenall_person ? `${r.hozenall_person}${r.hozenall_branch ? `（${r.hozenall_branch}）` : ''}` : '',
      },
      { field: 'participants_count', headerName: '人数', width: 60, align: 'right' },
      { field: 'expected_amount', headerName: '入金予定額', width: 100, align: 'right', valueFormatter: yen },
      {
        field: 'paid_amount',
        headerName: '入金額',
        width: 110,
        align: 'right',
        renderCell: (r: EventPaymentRow) => (
          <PaidAmountCell row={r} canEdit={canEditPayment} onSave={handleSavePaidAmount} />
        ),
      },
      {
        field: 'paid_date',
        headerName: '入金日',
        width: 120,
        // 入金額の消込時に自動記録。管理者は通帳記帳日等に合わせて手動修正できる
        renderCell: (r: EventPaymentRow) => (
          <InlineDateCell value={r.paid_date} canEdit={canEditPayment} width={130} onSave={(v) => handleSavePaidDate(r.id, v)} />
        ),
      },
      {
        field: 'difference',
        headerName: '差額',
        width: 100,
        align: 'right',
        renderCell: (r: EventPaymentRow) => (
          <Typography sx={{ fontSize: '13px', fontWeight: (r.difference || 0) !== 0 ? 700 : 400, color: (r.difference || 0) !== 0 ? '#b91c1c' : 'inherit' }}>
            {yen(r.difference)}
          </Typography>
        ),
      },
      {
        field: 'is_paid',
        headerName: '状況',
        width: 80,
        align: 'center',
        renderCell: (r: EventPaymentRow) =>
          canEditPayment ? (
            <Tooltip title="クリックで入金済⇔未入金を切替" arrow>
              <Box onClick={() => handleTogglePaid(r)} sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                {paidChip(r.is_paid)}
              </Box>
            </Tooltip>
          ) : (
            paidChip(r.is_paid)
          ),
      },
      {
        field: 'note',
        headerName: '備考',
        width: 200,
        renderCell: (r: EventPaymentRow) => (
          <InlineTextCell
            value={r.note}
            canEdit={canEditPayment}
            width={190}
            onSave={(v) => handleSavePaymentNote(r.id, v)}
          />
        ),
      },
    ],
    [canEditPayment, handleSavePaidAmount, handleSavePaidDate, handleTogglePaid, handleSavePaymentNote],
  );

  // 協賛品ごとの賞品への割当数 (賞品の授与の行数。協賛品一覧の「賞品割当」と賞品枠の作成に使う)
  const assignedBySponsorship = useMemo(() => {
    const map = new Map<number, number>();
    awards.forEach((a) => {
      if (a.sponsorship_id != null) map.set(a.sponsorship_id, (map.get(a.sponsorship_id) ?? 0) + 1);
    });
    return map;
  }, [awards]);

  const sponsorshipColumns: Column[] = useMemo(
    () => [
      // 受付NO・会社名を1列へまとめて左端に固定する (他タブと同一方式)
      {
        field: 'reception_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">受付NO</Box>
            <Box component="span">会社名 お客様名</Box>
          </Box>
        ),
        width: 300,
        sticky: true,
        renderCell: (r: EventSponsorshipRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: RECEPTION_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', color: '#64748b' }}>{r.reception_no}</Typography>
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.company_name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'hozenall_person',
        headerName: '当社担当',
        width: 140,
        // 申込フォームで入力された当社担当者 (協賛品側の受渡担当「担当者」列とは別)
        renderCell: (r: EventSponsorshipRow) =>
          r.hozenall_person ? `${r.hozenall_person}${r.hozenall_branch ? `（${r.hozenall_branch}）` : ''}` : '',
      },
      {
        field: 'product_name',
        headerName: '商品名',
        width: 180,
        renderCell: (r: EventSponsorshipRow) => (
          <InlineTextCell value={r.product_name} canEdit={canSendMail} width={170} onSave={(v) => handleSaveSponsorshipField(r.id, { product_name: v })} />
        ),
      },
      {
        field: 'quantity',
        headerName: '点数',
        width: 70,
        align: 'right',
        renderCell: (r: EventSponsorshipRow) => (
          <InlineTextCell
            value={r.quantity != null ? String(r.quantity) : null}
            canEdit={canSendMail}
            width={60}
            onSave={(v) => handleSaveSponsorshipQuantity(r.id, v)}
          />
        ),
      },
      {
        field: 'assigned',
        headerName: '賞品割当',
        width: 80,
        align: 'center',
        // 賞品の授与に割り当てた数 / 点数 (点数未入力は 1 点とみなして色を付ける)
        renderCell: (r: EventSponsorshipRow) => {
          const n = assignedBySponsorship.get(r.id) ?? 0;
          const full = n >= (r.quantity ?? 1);
          return (
            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: n === 0 ? '#94a3b8' : full ? '#059669' : '#d97706' }}>
              {n}/{r.quantity ?? '-'}
            </Typography>
          );
        },
      },
      { field: 'carry_method', headerName: 'お渡し方法', width: 100 },
      {
        field: 'scheduled_date',
        headerName: '持参予定日',
        width: 120,
        renderCell: (r: EventSponsorshipRow) => (
          <InlineDateCell value={r.scheduled_date} canEdit={canSendMail} width={130} onSave={(v) => handleSaveSponsorshipField(r.id, { scheduled_date: v })} />
        ),
      },
      {
        field: 'carried_date',
        headerName: '持参日',
        width: 120,
        renderCell: (r: EventSponsorshipRow) => (
          <InlineDateCell value={r.carried_date} canEdit={canSendMail} width={130} onSave={(v) => handleSaveSponsorshipField(r.id, { carried_date: v })} />
        ),
      },
      {
        field: 'storage_location',
        headerName: '保管場所',
        width: 120,
        renderCell: (r: EventSponsorshipRow) => (
          <InlineTextCell value={r.storage_location} canEdit={canSendMail} width={110} onSave={(v) => handleSaveSponsorshipField(r.id, { storage_location: v })} />
        ),
      },
      {
        field: 'staff',
        headerName: '受領者',
        width: 110,
        // 協賛品を受け取った当社側の担当者 (従業員マスタから選択、氏名文字列で保存)
        renderCell: (r: EventSponsorshipRow) => (
          <InlineStaffCell value={r.staff} canEdit={canSendMail} width={100} onSave={(v) => handleSaveSponsorshipField(r.id, { staff: v })} />
        ),
      },
      {
        field: 'received_date',
        headerName: '受領日',
        width: 120,
        // 受領日の入力で is_received も自動連動する (日付あり=受領済)
        renderCell: (r: EventSponsorshipRow) => (
          <InlineDateCell value={r.received_date} canEdit={canSendMail} width={130} onSave={(v) => handleSaveSponsorshipField(r.id, { received_date: v })} />
        ),
      },
      {
        field: 'thankyou_sent',
        headerName: '御礼状',
        width: 60,
        align: 'center',
        renderCell: (r: EventSponsorshipRow) =>
          canSendMail ? (
            <Tooltip title="クリックで送付済⇔未送付を切替" arrow>
              <Box onClick={() => handleToggleSponsorshipFlag(r, 'thankyou_sent')} sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                {circle(r.thankyou_sent)}
              </Box>
            </Tooltip>
          ) : (
            circle(r.thankyou_sent)
          ),
      },
      {
        field: 'thankyou_recipient',
        headerName: '御礼状送付先',
        width: 160,
        renderCell: (r: EventSponsorshipRow) => (
          <InlineTextCell value={r.thankyou_recipient} canEdit={canSendMail} width={150} onSave={(v) => handleSaveSponsorshipField(r.id, { thankyou_recipient: v })} />
        ),
      },
    ],
    [canSendMail, handleSaveSponsorshipField, handleSaveSponsorshipQuantity, handleToggleSponsorshipFlag, assignedBySponsorship],
  );

  const inviteeColumns: Column[] = useMemo(
    () => [
      { field: 'company_name', headerName: '会社名', width: 240, sticky: true },
      {
        field: 'person_name',
        headerName: '案内先',
        width: 140,
        renderCell: (r: EventInviteeRow) =>
          r.person_name ? (
            r.person_name
          ) : (
            <Chip size="small" label="法人宛" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#f1f5f9', color: '#64748b' }} />
          ),
      },
      { field: 'person_title', headerName: '役職', width: 130 },
      {
        field: 'business_email',
        headerName: 'メールアドレス',
        width: 220,
        // 配信先アドレス。個人は勤務先メール、法人宛行は法人マスタのメールを表示
        renderCell: (r: EventInviteeRow) => r.business_email ?? r.company_email ?? '',
      },
      {
        field: 'flags',
        headerName: '案内区分',
        width: 150,
        renderCell: (r: EventInviteeRow) => (
          <InviteeFlagsCell row={r} canEdit={canEditInvitee} onToggle={handleToggleInviteeFlag} />
        ),
      },
      {
        field: 'staff_name',
        headerName: '担当者',
        width: 110,
        // 個人顧客の担当者を優先、なければ法人顧客の担当者 (サーバー側で解決済み)
        renderCell: (r: EventInviteeRow) => r.staff_name || '',
      },
      {
        field: 'plan_status',
        headerName: '状態',
        width: 100,
        renderCell: (r: EventInviteeRow) => (
          <InviteeStatusCell row={r} canEdit={canEditInvitee} onSelect={handleSetInviteeStatus} />
        ),
      },
      { field: 'invited_at', headerName: '案内送付日', width: 90 },
      {
        field: 'note',
        headerName: '備考',
        width: 200,
        renderCell: (r: EventInviteeRow) => (
          <InlineTextCell
            value={r.note}
            canEdit={canEditInvitee}
            width={190}
            onSave={(v) => handleSaveInviteeText(r.id, 'note', v)}
          />
        ),
      },
      ...(canSendMail
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 110,
              align: 'center' as const,
              renderCell: (r: EventInviteeRow) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  {(r.business_email || r.company_email) &&
                    (sendingInviteeId === r.id ? (
                      <IconButton size="small" disabled>
                        <CircularProgress size={14} />
                      </IconButton>
                    ) : (
                      <Tooltip title={r.plan_status === '案内送付済' ? '案内メールを再送 (実行委員会名義)' : '案内メールを送信 (実行委員会名義)'} arrow>
                        <IconButton size="small" onClick={() => handleSendInviteeMail(r)} disabled={sendingInviteeId != null}>
                          <Icons.Send sx={{ fontSize: '16px', color: r.plan_status === '案内送付済' ? '#cbd5e1' : '#3b82f6' }} />
                        </IconButton>
                      </Tooltip>
                    ))}
                  {canSendMail && (r.customer_id || r.company_customer_id) && (
                    <Tooltip title="顧客情報を編集" arrow>
                      <IconButton size="small" onClick={() => setEditCustomerId(r.customer_id ?? r.company_customer_id)}>
                        <Icons.Edit sx={{ fontSize: '16px', color: '#94a3b8' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canEditInvitee ? (
                    <Tooltip title="配信予定から除外" arrow>
                      <IconButton size="small" onClick={() => handleDeleteInvitee(r)}>
                        <Icons.Delete sx={{ fontSize: '16px', color: '#94a3b8' }} />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="削除を依頼 (実行委員会Chatへ通知)" arrow>
                      <IconButton size="small" onClick={() => handleRequestDeleteInvitee(r)}>
                        <Icons.Delete sx={{ fontSize: '16px', color: '#94a3b8' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ),
            },
          ]
        : []),
    ],
    [canEditInvitee, canSendMail, handleToggleInviteeFlag, handleSaveInviteeText, handleSetInviteeStatus, handleDeleteInvitee, handleRequestDeleteInvitee, handleSendInviteeMail, sendingInviteeId],
  );

  // 配置のグループの呼び名 (成績の組・チーム列の見出しに使う)
  const groupLabel = event?.group_unit_label || '卓';

  // 参加者ごとの受賞 (成績タブの「賞」列に並べる)
  const awardNamesByParticipant = useMemo(() => {
    const map = new Map<number, string[]>();
    awards.forEach((a) => {
      if (a.participant_id != null) map.set(a.participant_id, [...(map.get(a.participant_id) ?? []), a.award_name]);
    });
    return map;
  }, [awards]);

  // チームのメンバー (配置のグループ番号 = チームの team_no。順番どおりに並べる)
  const teamMembers = useMemo(() => {
    const map = new Map<string, EventParticipantRow[]>();
    participants.forEach((p) => {
      if (p.table_no) map.set(p.table_no, [...(map.get(p.table_no) ?? []), p]);
    });
    map.forEach((list) => list.sort((a, b) => (a.seat_no ?? '').localeCompare(b.seat_no ?? '', 'ja', { numeric: true })));
    return map;
  }, [participants]);

  // 成績 (個人)。形式の項目定義 (fields) から列を組み立てるので、種別が増えても画面は共通
  const resultColumns: Column[] = useMemo(
    () => [
      {
        field: 'rank_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: RANK_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">順位</Box>
            <Box component="span">氏名</Box>
          </Box>
        ),
        width: 190,
        sticky: true,
        renderCell: (r: EventResultRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: RANK_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, textAlign: 'right', pr: 1 }}>{r.rank_no ?? '-'}</Typography>
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {personName(r)}
              {r.is_staff && <Box component="span" sx={{ fontSize: '11px', color: '#94a3b8', ml: 0.5 }}>社員</Box>}
            </Typography>
          </Box>
        ),
      },
      { field: 'company_name', headerName: '会社名', width: 220 },
      { field: 'table_no', headerName: groupLabel, width: 70 },
      ...(resultFormat?.fields ?? []).map((f) => ({
        field: `details.${f.key}`,
        headerName: f.label,
        width: f.type === 'number' ? 70 : 140,
        align: f.type === 'number' ? ('right' as const) : undefined,
        renderCell: (r: EventResultRow) => scoreText(r.details?.[f.key]),
      })),
      {
        field: 'score',
        headerName: resultFormat?.score_label ?? 'スコア',
        width: 80,
        align: 'right',
        renderCell: (r: EventResultRow) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{scoreText(r.score)}</Typography>,
      },
      {
        field: 'awards',
        headerName: '賞',
        width: 180,
        renderCell: (r: EventResultRow) => (r.participant_id ? (awardNamesByParticipant.get(r.participant_id) ?? []).join('・') : ''),
      },
      { field: 'note', headerName: '備考', width: 200 },
    ],
    [resultFormat, groupLabel, awardNamesByParticipant],
  );

  // チーム成績。メンバーは配置から引く (成績表にメンバーを重複して持たない)
  const teamResultColumns: Column[] = useMemo(
    () => [
      {
        field: 'rank_no',
        headerName: (
          <Box sx={{ display: 'grid', gridTemplateColumns: RANK_STICKY_GRID, gap: 1, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <Box component="span">順位</Box>
            <Box component="span">{groupLabel}</Box>
          </Box>
        ),
        width: 160,
        sticky: true,
        renderCell: (r: EventResultRow) => (
          <Box sx={{ display: 'grid', gridTemplateColumns: RANK_STICKY_GRID, gap: 1, alignItems: 'center' }}>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, textAlign: 'right', pr: 1 }}>{r.rank_no ?? '-'}</Typography>
            <Typography sx={{ fontSize: '13px' }}>{r.team_no}</Typography>
          </Box>
        ),
      },
      { field: 'team_name', headerName: 'チーム名', width: 160 },
      {
        field: 'members',
        headerName: 'メンバー',
        width: 420,
        renderCell: (r: EventResultRow) => ((r.team_no && teamMembers.get(r.team_no)) || []).map(personName).join('、'),
      },
      {
        field: 'score',
        headerName: resultFormat?.team_score_label ?? 'スコア',
        width: 90,
        align: 'right',
        renderCell: (r: EventResultRow) => <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{scoreText(r.score)}</Typography>,
      },
      { field: 'note', headerName: '備考', width: 200 },
    ],
    [resultFormat, groupLabel, teamMembers],
  );

  // 成績のタブは、成績の形式を設定したイベントか、成績・授与が登録済みのイベントだけに出す
  const showResults = !!event?.result_format_code || results.length > 0;

  // 賞品の授与 (表彰と抽選)。順位は成績から、協賛元は協賛品の申込から解決済み。
  // 当選者未定の賞品枠は当選者に「未定」を出す。総合順位 (個人成績の順位) は成績を記録するイベントだけ出す
  const awardColumns: Column[] = useMemo(
    () => [
      {
        field: 'sort_order',
        headerName: '発表順',
        width: 70,
        sticky: true,
        renderCell: (r: EventAwardRow) => (
          <InlineTextCell
            value={r.sort_order != null ? String(r.sort_order) : null}
            canEdit={canEditAward}
            width={56}
            onSave={(v) => handleSaveAwardOrder(r.id, v)}
          />
        ),
      },
      {
        field: 'award_name',
        headerName: '賞',
        width: 200,
        sticky: true,
        renderCell: (r: EventAwardRow) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {awardTypeChip(r.award_type)}
            <Typography sx={{ fontSize: '13px', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.award_name}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'last_name',
        headerName: '当選者・受賞者',
        width: 140,
        renderCell: (r: EventAwardRow) =>
          r.participant_id == null ? (
            <Chip size="small" label="未定" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#f1f5f9', color: '#64748b' }} />
          ) : (
            <Typography sx={{ fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {personName(r)}
              {r.is_staff && <Box component="span" sx={{ fontSize: '11px', color: '#94a3b8', ml: 0.5 }}>社員</Box>}
            </Typography>
          ),
      },
      { field: 'company_name', headerName: '会社名', width: 200 },
      { field: 'table_no', headerName: groupLabel, width: 60 },
      ...(showResults ? [{ field: 'rank_no', headerName: '総合順位', width: 72, align: 'right' as const }] : []),
      {
        field: 'image_url',
        headerName: '画像',
        width: 56,
        align: 'center',
        // 画像はクリックで拡大。付ける・差し替えるは編集ダイアログで行う
        renderCell: (r: EventAwardRow) =>
          r.image_url ? (
            <Box
              component="img"
              src={r.image_url}
              alt={r.prize_name ?? '賞品の画像'}
              loading="lazy"
              onClick={() => setAwardImagePreview(r)}
              sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 0.5, cursor: 'zoom-in', display: 'block', mx: 'auto' }}
            />
          ) : (
            ''
          ),
      },
      { field: 'prize_name', headerName: '賞品', width: 240 },
      {
        field: 'sponsor_company_name',
        headerName: '協賛元',
        width: 220,
        // 協賛品とつながっていない賞品は当社で用意したもの
        renderCell: (r: EventAwardRow) =>
          r.sponsorship_id ? (
            r.sponsor_company_name ?? ''
          ) : (
            <Typography sx={{ fontSize: '12px', color: '#94a3b8' }}>当社用意</Typography>
          ),
      },
      {
        field: 'is_received',
        headerName: '受け取り',
        width: 80,
        align: 'center',
        renderCell: (r: EventAwardRow) =>
          canEditAward && r.participant_id != null ? (
            <Tooltip title="クリックで受け取り済⇔未を切替" arrow>
              <Box onClick={() => handleToggleAwardReceived(r)} sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                {circle(r.is_received)}
              </Box>
            </Tooltip>
          ) : (
            circle(r.is_received)
          ),
      },
      { field: 'note', headerName: '備考', width: 200 },
      ...(canEditAward
        ? [
            {
              field: 'actions',
              headerName: '',
              width: 80,
              align: 'center' as const,
              renderCell: (r: EventAwardRow) => (
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Tooltip title="賞品・当選者を編集" arrow>
                    <IconButton size="small" onClick={() => setAwardDialog({ mode: 'edit', award: r })}>
                      <Icons.Edit sx={{ fontSize: '16px', color: '#94a3b8' }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="この行を削除" arrow>
                    <IconButton size="small" onClick={() => handleDeleteAward(r)}>
                      <Icons.Delete sx={{ fontSize: '16px', color: '#94a3b8' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            },
          ]
        : []),
    ],
    [canEditAward, groupLabel, showResults, handleSaveAwardOrder, handleToggleAwardReceived, handleDeleteAward],
  );

  // 賞品の授与のタブは、成績を記録するイベント・授与が登録済みのイベントに加え、
  // 協賛品を賞品に充てるイベント (忘年会の抽選など) にも出す
  const showAwards = showResults || awards.length > 0 || sponsorships.length > 0;
  const tabDefs: { key: TabKey; label: string; count: number; columns: Column[]; rows: any[] }[] = [
    { key: 'entries', label: '申込一覧', count: filtered.entries.length, columns: entryColumns, rows: filtered.entries },
    { key: 'participants', label: '参加者一覧', count: filtered.participants.length, columns: participantColumns, rows: filtered.participants },
    { key: 'payments', label: '入金一覧', count: filtered.payments.length, columns: paymentColumns, rows: filtered.payments },
    { key: 'sponsorships', label: '協賛品一覧', count: filtered.sponsorships.length, columns: sponsorshipColumns, rows: filtered.sponsorships },
    { key: 'invitees', label: '配信予定', count: filtered.invitees.length, columns: inviteeColumns, rows: filtered.invitees },
    ...(showResults
      ? [{ key: 'results' as const, label: '成績', count: filtered.results.length, columns: resultColumns, rows: filtered.results }]
      : []),
    ...(teamResults.length > 0
      ? [{ key: 'teamResults' as const, label: 'チーム成績', count: filtered.teamResults.length, columns: teamResultColumns, rows: filtered.teamResults }]
      : []),
    ...(showAwards
      ? [{ key: 'awards' as const, label: '賞品の授与', count: filtered.awards.length, columns: awardColumns, rows: filtered.awards }]
      : []),
  ];
  const activeTab = tabDefs.find((t) => t.key === tab) ?? tabDefs[0];
  const pagedRows = activeTab.rows.slice(page * pageSize, page * pageSize + pageSize);

  // 案内メールタブの差し込みプレビュー用サンプル宛先
  // (一斉メール希望の個人行のうち、役職まで揃っている宛先を優先して全差し込みを確認できるようにする)
  const sampleInvitee = useMemo(
    () =>
      invitees.find((r) => r.request_bulk_mail && r.person_name && r.company_name && r.person_title) ??
      invitees.find((r) => r.request_bulk_mail && r.person_name && r.company_name) ??
      invitees.find((r) => r.person_name && r.company_name) ??
      null,
    [invitees],
  );

  const summaryCards = [
    {
      title: '申込 (出席)',
      value: `${summary.attendCount.toLocaleString()}`,
      unit: `件 / 欠席 ${summary.absentCount} 件`,
      icon: <Icons.ListAlt sx={{ fontSize: '18px' }} />,
      color: '#eff6ff',
      iconColor: '#3b82f6',
    },
    {
      title: '参加者数',
      value: `${summary.attendParticipants.toLocaleString()}`,
      unit: '名',
      icon: <Icons.Groups sx={{ fontSize: '18px' }} />,
      color: '#ecfdf5',
      iconColor: '#059669',
    },
    {
      title: '入金済み',
      value: `${summary.paidCount.toLocaleString()}`,
      unit: `件 / 未入金 ${summary.unpaidCount} 件`,
      icon: <Icons.Payments sx={{ fontSize: '18px' }} />,
      color: '#f5f3ff',
      iconColor: '#8b5cf6',
    },
    {
      title: '協賛',
      value: `${summary.sponsorCount.toLocaleString()}`,
      unit: '件',
      icon: <Icons.Handshake sx={{ fontSize: '18px' }} />,
      color: '#fffbeb',
      iconColor: '#d97706',
    },
    {
      title: '案内メール未送',
      value: `${summary.mailUnsent.toLocaleString()}`,
      unit: '件',
      icon: <Icons.EmailOutlined sx={{ fontSize: '18px' }} />,
      color: summary.mailUnsent > 0 ? '#fee2e2' : '#f1f5f9',
      iconColor: summary.mailUnsent > 0 ? '#b91c1c' : '#64748b',
    },
  ];

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', height: PAGE_FILL_HEIGHT, boxSizing: 'border-box', overflow: 'hidden' }}>
      <Box sx={{ width: '92%', mt: 0, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minHeight: 0 }}>
        {/* 見出し + イベント切替 + 更新 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
              <Icons.EventAvailable /> {event?.event_name ?? 'イベント詳細'}
              {event && (
                <Typography component="span" sx={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600 }}>
                  {event.event_code}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: '#475569', display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {event ? (
                <>
                  <span>開催日 {event.event_date ?? '未定'}</span>
                  <span>／ 会場 {event.venue ?? '未定'}</span>
                  <span>／ 会費 当日 {yen(event.fee_regular)}・事前振込 {yen(event.fee_prepaid)}</span>
                  {event.is_active && (
                    <Chip size="small" label="公開中" sx={{ fontSize: '11px', height: '20px', fontWeight: 700, bgcolor: '#ecfdf5', color: '#059669' }} />
                  )}
                </>
              ) : (
                '忘年会などのイベント申込状況を一覧で確認します'
              )}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button onClick={() => navigate('/event-management')} startIcon={<Icons.ArrowBack />} sx={STANDARD_TEXT_BUTTON_SX}>
              イベント一覧へ
            </Button>
            <Fab variant="extended" size="small" disabled={loading} onClick={reload} sx={STANDARD_FAB_CTA_SX}>
              <Icons.Refresh />
              最新に更新
            </Fab>
          </Box>
        </Box>

        {error && <Alert severity="warning" sx={{ width: '100%', borderRadius: '8px' }}>{error}</Alert>}

        {/* サマリーカード */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5 }}>
          {summaryCards.map((c, idx) => (
            <Card key={idx} sx={{ borderRadius: '10px', boxShadow: '0 2px 4px -1px rgb(0 0 0 / 0.08)', border: '1px solid #f1f5f9' }}>
              <CardContent sx={{ px: 1.75, py: 1, '&:last-child': { pb: 1 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ p: 0.6, borderRadius: '8px', backgroundColor: c.color, color: c.iconColor, display: 'flex' }}>
                    {c.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#64748b', lineHeight: 1.2 }}>
                      {c.title}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography sx={{ fontSize: '18px', fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                        {c.value}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#64748b', whiteSpace: 'nowrap' }}>
                        {c.unit}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* タブ + 検索 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          {/* タブが画面幅に入りきらないときは横スクロール + 左右の矢印で送る */}
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 36,
              minWidth: 0,
              maxWidth: '100%',
              '& .MuiTab-root': { minHeight: 36, fontSize: '13px', fontWeight: 700, textTransform: 'none', px: 1.75 },
              '& .MuiTabs-scrollButtons': { width: 28 },
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
            }}
          >
            {tabDefs
              .filter((t) => !RECORD_TAB_KEYS.includes(t.key))
              .map((t) => (
                <Tab key={t.key} value={t.key} label={`${t.label} (${t.count.toLocaleString()})`} />
              ))}
            {/* 配置 (組合表 / 席次表 / 投順表)。見出しはイベント側の呼び名 */}
            <Tab value="assignments" label={`${event?.group_unit_label || '卓'}割 (${participants.filter((p) => p.table_no).length.toLocaleString()})`} />
            {/* 開催後の記録 (成績・賞品の授与) は配置の後ろに並べる */}
            {tabDefs
              .filter((t) => RECORD_TAB_KEYS.includes(t.key))
              .map((t) => (
                <Tab key={t.key} value={t.key} label={`${t.label} (${t.count.toLocaleString()})`} />
              ))}
            <Tab value="mail" label="案内メール" />
          </Tabs>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {tab === 'entries' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {ENTRY_FILTERS.map((f) => (
                  <Chip
                    key={f.key}
                    size="small"
                    label={f.label}
                    clickable
                    onClick={() => handleEntryFilter(f.key)}
                    sx={{
                      fontSize: '11px',
                      height: '24px',
                      fontWeight: 700,
                      ...(entryFilter === f.key
                        ? { bgcolor: '#1e293b', color: '#fff', '&:hover': { bgcolor: '#334155' } }
                        : { bgcolor: '#f1f5f9', color: '#64748b' }),
                    }}
                  />
                ))}
              </Box>
            )}
            {tab === 'invitees' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {INVITEE_FILTERS.map((f) => (
                  <Chip
                    key={f.key}
                    size="small"
                    label={f.label}
                    clickable
                    onClick={() => handleInviteeFilter(f.key)}
                    sx={{
                      fontSize: '11px',
                      height: '24px',
                      fontWeight: 700,
                      ...(inviteeFilter === f.key
                        ? { bgcolor: '#1e293b', color: '#fff', '&:hover': { bgcolor: '#334155' } }
                        : { bgcolor: '#f1f5f9', color: '#64748b' }),
                    }}
                  />
                ))}
                {canSendMail && (
                  <Fab variant="extended" size="small" onClick={() => setAddInviteeOpen(true)} sx={{ ...STANDARD_FAB_CTA_SX, ml: 0.5 }}>
                    <Icons.PersonAdd sx={{ fontSize: '18px', mr: 0.5 }} />
                    案内先を追加
                  </Fab>
                )}
              </Box>
            )}
            {tab === 'awards' && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                {AWARD_FILTERS.map((f) => (
                  <Chip
                    key={f.key}
                    size="small"
                    label={f.label}
                    clickable
                    onClick={() => handleAwardFilter(f.key)}
                    sx={{
                      fontSize: '11px',
                      height: '24px',
                      fontWeight: 700,
                      ...(awardFilter === f.key
                        ? { bgcolor: '#1e293b', color: '#fff', '&:hover': { bgcolor: '#334155' } }
                        : { bgcolor: '#f1f5f9', color: '#64748b' }),
                    }}
                  />
                ))}
                {canEditAward && (
                  <>
                    <Fab
                      variant="extended"
                      size="small"
                      onClick={() => setAwardOrderOpen(true)}
                      disabled={awards.length < 2}
                      sx={{ ...STANDARD_FAB_CTA_SX, ml: 0.5 }}
                    >
                      <Icons.Sort sx={{ fontSize: '18px', mr: 0.5 }} />
                      発表順を並べ替え
                    </Fab>
                    <Fab variant="extended" size="small" onClick={() => setAwardSlotsOpen(true)} sx={STANDARD_FAB_CTA_SX}>
                      <Icons.Redeem sx={{ fontSize: '18px', mr: 0.5 }} />
                      協賛品から賞品枠を作る
                    </Fab>
                    <Fab variant="extended" size="small" onClick={() => setAwardDialog({ mode: 'add' })} sx={STANDARD_FAB_CTA_SX}>
                      <Icons.CardGiftcard sx={{ fontSize: '18px', mr: 0.5 }} />
                      賞品を追加
                    </Fab>
                  </>
                )}
              </Box>
            )}
            {tab !== 'mail' && tab !== 'assignments' && <SearchInput onCommit={handleFilterCommit} />}
          </Box>
        </Box>

        {/* テーブル領域 (残り高さいっぱい・テーブル内スクロール)。案内メールタブは文面・添付の管理パネル */}
        {tab === 'mail' ? (
          event && (
            <EventMailPanel event={event} sampleInvitee={sampleInvitee} canEdit={canEditInvitee} onSave={updateEventMail} />
          )
        ) : tab === 'assignments' ? (
          event && (
            <EventAssignmentPanel
              event={event}
              participants={participants}
              loading={loading}
              canEdit={canEditInvitee}
              onSave={saveAssignments}
              onAddStaff={addStaffParticipants}
              onRemoveStaff={removeStaffParticipant}
            />
          )
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <StandardDataTable
              scrollContainerRef={tableScrollRef}
              rows={pagedRows}
              columns={activeTab.columns}
              loading={loading}
              loadingMessage="イベントデータを取得中"
              totalCount={activeTab.rows.length}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              onSortChange={() => undefined}
            />
          </Box>
        )}
      </Box>

      <AddInviteeDialog open={addInviteeOpen} onClose={() => setAddInviteeOpen(false)} onAdd={handleAddInvitee} />

      {participantDialog && (
        <EventParticipantDialog
          open
          mode={participantDialog.mode}
          entry={participantDialog.entry}
          participant={participantDialog.participant}
          eventName={event?.event_name ?? ''}
          participants={participants}
          onClose={() => setParticipantDialog(null)}
          onSave={handleSaveParticipant}
        />
      )}

      {awardDialog && (
        <EventAwardDialog
          open
          mode={awardDialog.mode}
          award={awardDialog.award}
          participants={participants}
          sponsorships={sponsorships}
          assignedBySponsorship={assignedBySponsorship}
          groupLabel={groupLabel}
          onClose={() => setAwardDialog(null)}
          onSave={handleSaveAward}
        />
      )}

      <EventAwardSlotsDialog
        open={awardSlotsOpen}
        sponsorships={sponsorships}
        assignedBySponsorship={assignedBySponsorship}
        onClose={() => setAwardSlotsOpen(false)}
        onCreate={handleCreateAwardSlots}
      />

      <EventAwardOrderDialog
        open={awardOrderOpen}
        awards={awards}
        onClose={() => setAwardOrderOpen(false)}
        onSave={handleReorderAwards}
      />

      {/* 賞品の画像の拡大表示 (一覧のサムネイルから) */}
      <Dialog open={!!awardImagePreview} onClose={() => setAwardImagePreview(null)} maxWidth="md" disableScrollLock>
        <DialogTitle sx={{ fontSize: '0.875rem', fontWeight: 700, py: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Icons.Image sx={{ fontSize: 18 }} />
          {awardImagePreview ? `${awardImagePreview.award_name}　${awardImagePreview.prize_name ?? ''}` : ''}
          <IconButton size="small" onClick={() => setAwardImagePreview(null)} sx={{ ml: 'auto' }}>
            <Icons.Close sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', pb: 2 }}>
          {awardImagePreview?.image_url && (
            <Box
              component="img"
              src={awardImagePreview.image_url}
              alt={awardImagePreview.prize_name ?? '賞品の画像'}
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* 配信予定行からの顧客情報編集 (CRM 共通フォーム)。保存後は JOIN 表示を最新化するため再取得 */}
      {editCustomerId && (
        <CrmCustomerForm
          customerId={editCustomerId}
          open
          onClose={() => setEditCustomerId(null)}
          onSuccess={() => {
            setEditCustomerId(null);
            reload();
          }}
        />
      )}
    </Box>
  );
}
